import React from 'react';
import { PreviewSvgModelInstance } from '../../../store/_models/previewSvg';
import { PreviewCanvasModelInstance } from '../../../store/_models/previewCanvas';
import CanvasPreview from '@photosi/rn-components/product-preview/canvas-preview/CanvasPreview';
import FramePreview from '@photosi/rn-components/product-preview/frame-preview/FramePreview';
import LegerPreview from '@photosi/rn-components/product-preview/leger-preview/LegerPreview';
import { PREVIEW_PRODUCT_TYPES } from 'src/store/_models/preview.constants';
import PhotoPreview from '@photosi/rn-components/product-preview/photo-preview/PhotoPreview';
import { View } from 'react-native';
import Animated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { ProductPreviewProps } from '../ProductPreview.types';
import { styles } from './Preview.styles';
import { PreviewPrintModelInstance } from 'src/store/_models/previewPrint';

export const Preview = ({
  productType,
  preview,
  editable = false,
  showGrid = false,
  swappable = false,
  disabled = false,
  disableSwapImageAnimation = false,
  renderOverlays,
  shouldUseContainerDimensions = false,
  showOverlays = false,
  isHighlighted = false,
  showBadges = true,
  userTexts = [],
}: ProductPreviewProps) => {
  let previewComponent;

  switch (productType) {
    case PREVIEW_PRODUCT_TYPES.CANVAS:
      previewComponent = (
        <CanvasPreview
          preview={preview as PreviewCanvasModelInstance}
          disabled={disabled}
          editable={editable}
          showGrid={showGrid}
          disableSwapImageAnimation={disableSwapImageAnimation}
          isHighlighted={isHighlighted}
          showBadges={showBadges}
        />
      );
      break;
    case PREVIEW_PRODUCT_TYPES.LEGER:
      previewComponent = (
        <LegerPreview
          preview={preview as PreviewSvgModelInstance}
          disabled={disabled}
          editable={editable}
          showGrid={showGrid}
          swappable={swappable}
          disableSwapImageAnimation={disableSwapImageAnimation}
          isHighlighted={isHighlighted}
          showBadges={showBadges}
        />
      );
      break;
    case PREVIEW_PRODUCT_TYPES.PHOTO_CLASSIC:
    case PREVIEW_PRODUCT_TYPES.POSTER:
    case PREVIEW_PRODUCT_TYPES.PHOTO_CLASSIC_WITH_BORDER:
    case PREVIEW_PRODUCT_TYPES.PHOTO_PANORAMIC:
    case PREVIEW_PRODUCT_TYPES.PHOTO_VINTAGE:
      previewComponent = (
        <PhotoPreview
          preview={preview as PreviewPrintModelInstance}
          disabled={disabled}
          editable={editable}
          showGrid={showGrid}
          disableSwapImageAnimation={disableSwapImageAnimation}
          isHighlighted={isHighlighted}
          showBadges={showBadges}
          userTexts={userTexts}
        />
      );
      break;
    case PREVIEW_PRODUCT_TYPES.FRAME:
    default:
      previewComponent = (
        <FramePreview
          preview={preview as PreviewSvgModelInstance}
          disabled={disabled}
          editable={editable}
          showGrid={showGrid}
          swappable={swappable}
          disableSwapImageAnimation={disableSwapImageAnimation}
          isHighlighted={isHighlighted}
          showBadges={showBadges}
        />
      );
  }

  // Animated style for overlay visibility
  const overlayAnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    if (typeof showOverlays === 'object' && showOverlays?.value !== undefined) {
      // SharedValue case - animated
      return {
        opacity: withTiming(showOverlays.value ? 1 : 0, { duration: 300 }),
        pointerEvents: showOverlays.value ? 'auto' : 'none',
      };
    } else if (typeof showOverlays === 'boolean') {
      // Boolean case - static
      return {
        opacity: showOverlays ? 1 : 0,
        pointerEvents: showOverlays ? 'auto' : 'none',
      };
    }
    // Default case - hide overlays (false)
    return {
      opacity: 0,
      pointerEvents: 'none',
    };
  }, [showOverlays]);

  // Call renderOverlays function to get the overlays array
  const overlays = renderOverlays ? renderOverlays() : undefined;

  //if there're no overlay, just return the component
  if (!overlays || overlays.length === 0) {
    return previewComponent;
  }

  // Separates the overlays by position
  const topOverlays = overlays.filter(overlay => !overlay.position || overlay.position === 'top');
  const bottomOverlays = overlays.filter(overlay => overlay.position === 'bottom');
  const centerOverlays = overlays.filter(overlay => overlay.position === 'center');

  // Wrap and map the overlays with the previewComponent inside a relative view
  return (
    <View style={styles.previewWrapper}>
      {/* Top position Overlays*/}
      {topOverlays.map((overlay, index) => {
        return (
          <Animated.View
            key={`top-overlay-${index}`}
            style={[styles.overlayContainer, styles.topOverlay, { zIndex: overlay.zIndex ?? 1 }, overlayAnimatedStyle]}
          >
            {overlay.render()}
          </Animated.View>
        );
      })}

      {centerOverlays?.map((overlay, index) => {
        return (
          <Animated.View
            key={`center-overlay-${index}`}
            style={[styles.overlayContainer, { zIndex: overlay.zIndex ?? 1 }, overlayAnimatedStyle]}
          >
            {overlay.render()}
          </Animated.View>
        );
      })}

      {/* The base component */}
      {previewComponent}

      {/* Bottom Position Overlays */}
      {bottomOverlays.map((overlay, index) => {
        return (
          <Animated.View
            key={`bottom-overlay-${index}`}
            style={[
              styles.overlayContainer,
              styles.bottomOverlay,
              overlay.zIndex ? { zIndex: overlay.zIndex } : {},
              overlayAnimatedStyle,
            ]}
          >
            {overlay.render()}
          </Animated.View>
        );
      })}
    </View>
  );
};

/**
 * TODO: Possibile refactoring!
 * We will choose the best approach after receiving more previews
 */

// const extraPropsByType = {
//   [PREVIEW_PRODUCT_TYPES.CANVAS]: { disableSwapImageAnimation },
//   [PREVIEW_PRODUCT_TYPES.FRAME]: { swappable, disableSwapImageAnimation },
//   [PREVIEW_PRODUCT_TYPES.LEGER]: {},
//   [PREVIEW_PRODUCT_TYPES.PHOTO_CLASSIC]: {},
// };
//
// const previewTypeMap = {
//   [PREVIEW_PRODUCT_TYPES.CANVAS]: CanvasPreview,
//   [PREVIEW_PRODUCT_TYPES.LEGER]: LegerPreview,
//   [PREVIEW_PRODUCT_TYPES.PHOTO_CLASSIC]: PhotoClassicPreview,
//   [PREVIEW_PRODUCT_TYPES.FRAME]: FramePreview,
// };
//
// const PreviewComponent = previewTypeMap[productType] || FramePreview;
//
// // Decidere il tipo di preview da fare cast:
// const isCanvas = productType === PREVIEW_PRODUCT_TYPES.CANVAS;
// const previewTyped = isCanvas
//   ? (preview as PreviewCanvasModelInstance)
//   : (preview as PreviewSvgModelInstance);
//
// return (
//   <PreviewComponent
//     preview={previewTyped}
//     {...commonProps}
//     {...(extraPropsByType[productType] || {})}
//   />
// );
